import { JobService } from './../../../services/job/job.service';
import { PaymentService } from './../../../services/payment/payment.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LOCK_SYMBOL_CODE, SECURE_ENCRYPTION_MSG, TERMS, UPDATE_BILLING_DESCRIPTION } from './update-billing.constants';
import { RouterService } from '../../../services/router/router.service';
import { Router } from '@angular/router';
import { APP_ROUTES } from '../../../utils/route.constant';
import { IBillingRequest, IBillingResponse, IContractData } from '../../../interfaces/IJob';
import { Subject, takeUntil } from 'rxjs';
import { take } from 'rxjs/operators';
import { DataService } from '../../../services/data/data.service';
import { expirationDateValidator, monthValidator, routingNumberValidator, yearValidator } from '../../../utils/validators';
import { UtilitiesService } from '../../../services/utilities/utilities.service';
import { ACH_AUTO, BANK_ACC_NO_REGEX, BANK_ROUTING_NO_REGEX, MONTHS, PAGE_SOURCE, PAYMENTECH } from '../../../utils/constants';
import { IMessage } from '../../../services/messages/messages.interface';
import { MessagesService } from '../../../services/messages/messages.service';
import { Ga4Service } from '../../../services/ga4/ga4.service';
import { BILLING_PREFERENCES_UPDATED_BANK, BILLING_PREFERENCES_UPDATED_CC, BUTTON_LOCATION_PAYMENTS_FROM_DASHBOARD, BUTTON_LOCATION_PAYMENTS_FROM_HISTORY, BUTTON_LOCATION_PAYMENTS_FROM_MYPLANS, BUTTON_LOCATION_PAYMENTS_FROM_PROFILE, BUTTON_LOCATION_PAYMENTS_FROM_SERVICE_REQUESTS, BUTTON_LOCATION_PAYMENTS_FROM_UNKNOWN, ERR_EVENT, UPDATE_BILLING_INFO_ERROR_CONTACT_NUMBER, UPDATE_BILLING_INFO_FAILURE_MESSAGE_DISPLAYED, UPDATE_BILLING_INFO_PAGE_LOCATION } from '../../../services/ga4/ga4.constant';
declare const ka: any;
@Component({
  selector: 'app-update-billing',
  templateUrl: './update-billing.component.html',
  styleUrl: './update-billing.component.scss'
})
export class UpdateBillingComponent implements OnInit, OnDestroy {
  bankAccountForm!: FormGroup;
  creditCardForm!: FormGroup;
  billingInformationForm!: FormGroup;
  tncCheck: boolean = false;
  showTnC: boolean = false;
  terms = TERMS;
  value = 0;
  contractData: IContractData | null = null;
  cleaner$ = new Subject<void>();
  paymentType: string = '';
  billingMonth: string = '';
  billingDay: string = '';
  updateBillingInfoPhone: string = UPDATE_BILLING_INFO_ERROR_CONTACT_NUMBER;
  updateBillingInfoPageLocation: string = UPDATE_BILLING_INFO_PAGE_LOCATION;
  isSubmitting: boolean = false;
  pageSource: string | null = '';
  showToast: boolean = false;
  showUpdateBillingInfoErrorToast: boolean = false;
  updateBillingDescription = UPDATE_BILLING_DESCRIPTION;
  secureEncryptionMessage= SECURE_ENCRYPTION_MSG;
  lockSymbol: string = LOCK_SYMBOL_CODE;
  private navigateBackTimeoutId?: ReturnType<typeof setTimeout>;
  updateBillingInfoErrorMessage: { message: string, link: string, linkText: string } | null = null;

  constructor(
    private data: DataService,
    private router: Router,
    private routerService: RouterService,
    private paymentService: PaymentService,
    private messages: MessagesService,
    private jobService:JobService,
    private gaService : Ga4Service,
    private util: UtilitiesService,
  ) {}

  ngOnInit(): void {
    this.scrollToTop();
    this.paymentService.clearForms();
    this.creditCardForm = this.paymentService.ccForm;
    this.bankAccountForm = this.paymentService.bankAccForm;
    this.billingInformationForm = this.paymentService.billingInfoForm;
    this.pageSource = this.data.navigationSource$.getValue();

    this.initializeForms();
    const url = this.router.url;
    this.data.contractData$
      .pipe(takeUntil(this.cleaner$))
      .subscribe(contract => {
        if(contract){
          this.contractData = contract
          this.updateFormAndView();
        }else{
          this.routerService.goto(APP_ROUTES.DASHBOARD);
        }
      });

    this.data.removeToast$
      .pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: () => {
          this.showUpdateBillingInfoErrorToast = false;
        },
      });  
  }

  scrollToTop(): void {
    window.scrollTo(0, 0);
  }

  initializeForms(): void {
    this.creditCardForm = this.paymentService.initiateCreditCardForm();
    this.bankAccountForm = this.paymentService.initiateBankAccountForm();
    this.billingInformationForm = this.paymentService.initiateBillingInformationForm();
  }

  updateFormAndView(): void{
    this.value = this.contractData?.paymentInfo.billMechCode === PAYMENTECH ? 1 : 0;
    this.paymentType = this.contractData?.paymentInfo.billMechCode === PAYMENTECH ? 'Credit Card' :'Bank Account';
    if (this.contractData && this.contractData.balanceInfo.chargeDateFormatUS) {
      const month: any = this.contractData.balanceInfo.chargeDateFormatUS.substring(0, 2);
      this.billingMonth = month && MONTHS[month - 1];

      const day = this.contractData.balanceInfo.chargeDateFormatUS.substring(3, 5);
      const suffixes: { [key: string]: string } = { '01': '1st', '02': '2nd', '03': '3rd' };
      this.billingDay = suffixes[day] ?? day + 'th';
    }

    this.creditCardForm.patchValue({
      firstName: this.contractData?.paymentInfo.creditCardFirstName ?? '',
      lastName: this.contractData?.paymentInfo.creditCardLastName ?? '',
      cardNumber: '',
      expirationMonth: '',
      expirationYear: '',
      cvv: '',
      typeCode: ''
    })

    this.bankAccountForm.patchValue({
      routingNumber: '',
      accountNumber: '',
      accountTypes:  null
    })


    this.billingInformationForm.patchValue({
      address: this.contractData?.billingAddress.addressLine1,
      address2: this.contractData?.billingAddress.addressLine2,
      city: this.contractData?.billingAddress.city,
      state: this.contractData?.billingAddress.state,
      zipCode: this.contractData?.billingAddress.zip
    });

  }

  updatePaymentType(tabIndex: string | number){
    this.paymentType = Number(tabIndex) === 1 ? 'Credit Card' :'Bank Account';
  }

  totalPriceYear(): string {
    const totalPremium = parseFloat(this.contractData?.baseContract?.totalPremiumPrice || '0');
    const salesTax = parseFloat(this.contractData?.baseContract?.salesTax || '0');
    return (totalPremium + salesTax).toFixed(2);
  }

  navigateBack(): void {
    let destination = APP_ROUTES.DASHBOARD;

    switch (this.pageSource) {
      case PAGE_SOURCE.PAYMENTS:
        destination = APP_ROUTES.PAYMENTS;
        break;

      case PAGE_SOURCE.PROFILE:
        destination = APP_ROUTES.PROFILE;
        break;

      case PAGE_SOURCE.MY_PLAN:
        destination = APP_ROUTES.MY_PLAN;
        break;

      case PAGE_SOURCE.SERVICE_REQUESTS:
        destination = APP_ROUTES.MY_SERVICE_REQUEST;
        break;

      default:
        destination = APP_ROUTES.DASHBOARD;
        break;
    }

    this.routerService.goto(destination);
  }

  disableSaveButton(): boolean{
    if (this.value === 0){
      return this.bankAccountForm.invalid || this.billingInformationForm.invalid || !this.tncCheck;
    }else{
      return this.creditCardForm.invalid || this.billingInformationForm.invalid || !this.tncCheck;
    }
  }

  createBillingRequest(): IBillingRequest{
    let billingRequest: IBillingRequest = {
      paymentInfo: this.contractData?.paymentInfo!,
      billingAddress: this.contractData?.billingAddress!,
      termsAgree: false
    }
    if (this.value === 1){
      billingRequest.paymentInfo.billMechCode = PAYMENTECH;
      billingRequest.paymentInfo.creditCardFirstName = this.creditCardForm.controls['firstName'].value;
      billingRequest.paymentInfo.creditCardLastName = this.creditCardForm.controls['lastName'].value;
      billingRequest.paymentInfo.creditCardNumber = this.creditCardForm.controls['cardNumber'].value;
      billingRequest.paymentInfo.creditCardExpMonth = this.creditCardForm.controls['expirationMonth'].value;
      billingRequest.paymentInfo.creditCardExpYear = this.creditCardForm.controls['expirationYear'].value;
      billingRequest.paymentInfo.creditCardTypeCode = this.creditCardForm.controls['typeCode'].value.toUpperCase();
      billingRequest.paymentInfo.creditCardCVV = this.creditCardForm.controls['cvv'].value;
      billingRequest.paymentInfo.bankAbaNumber = '';
      billingRequest.paymentInfo.bankAcctNumber = '';
      billingRequest.paymentInfo.bankAcctType = '';
      //this.bankAccountForm.reset();
    }else{
      billingRequest.paymentInfo.billMechCode = ACH_AUTO;
      billingRequest.paymentInfo.creditCardFirstName = '';
      billingRequest.paymentInfo.creditCardLastName ='';
      billingRequest.paymentInfo.creditCardNumber = '';
      billingRequest.paymentInfo.creditCardExpMonth = 0;
      billingRequest.paymentInfo.creditCardExpYear = 0;
      billingRequest.paymentInfo.creditCardTypeCode = '';
      billingRequest.paymentInfo.creditCardCVV = 0;
      billingRequest.paymentInfo.bankAbaNumber = this.bankAccountForm.controls['routingNumber'].value;
      billingRequest.paymentInfo.bankAcctNumber = this.bankAccountForm.controls['accountNumber'].value;
      billingRequest.paymentInfo.bankAcctType = this.bankAccountForm.controls['accountTypes'].value;
      //this.creditCardForm.reset();
    }

    billingRequest.paymentInfo.safetechSessionId = ka.sessionId;

    // contract number
    billingRequest.paymentInfo.contractNumber = this.contractData?.baseContract.contractNumber!;
    // billing address
    billingRequest.billingAddress.addressLine1 = this.billingInformationForm.controls['address'].value;
    billingRequest.billingAddress.addressLine2 = this.billingInformationForm.controls['address2'].value;
    billingRequest.billingAddress.city = this.billingInformationForm.controls['city'].value;
    billingRequest.billingAddress.state = this.billingInformationForm.controls['state'].value;
    billingRequest.billingAddress.zip = this.billingInformationForm.controls['zipCode'].value;

    billingRequest.termsAgree = this.tncCheck;

    return billingRequest;
  }



  submitForm(): void {
    const billingRequest = this.createBillingRequest();
    this.isSubmitting = true;
    this.setGaButtonClickEvent();
    this.paymentService.saveBillingInformation(billingRequest, billingRequest.paymentInfo.contractNumber)
      .pipe(takeUntil(this.cleaner$))
      .subscribe({
        next: (response: IBillingResponse) => {
          this.jobService
            .getContractData(response.paymentInfo.contractNumber, false)
            .pipe(takeUntil(this.cleaner$))
            .subscribe({
              next: () => {
                this.showToast = true;
                this.scrollToTop();
                // ✅ Disable all forms (removes validation + prevents interaction)
                this.creditCardForm.disable();
                this.bankAccountForm.disable();
                this.billingInformationForm.disable();
                this.navigateBackTimeoutId = setTimeout(() => {
                  this.isSubmitting = false;
                  this.navigateBack();
                  this.creditCardForm.enable();
                  this.bankAccountForm.enable();
                  this.billingInformationForm.enable();
                }, 3000);
              },
              error: (err) => {
                this.isSubmitting = false;

                const errorMessage: IMessage = {
                  severity: 'error',
                  text: 'Unable to refresh contract data. Please try again.',
                  icon: 'pi pi-ban'
                };

                this.messages.addMessages(errorMessage);
              }
            });
        },
        error: (err: HttpErrorResponse) => {
          this.isSubmitting = false;
          console.error('Error updating billing information:', err);
          this.setUpdateBillingInfoErrorToastMessage();
          this.gaService.messageEvent(ERR_EVENT, UPDATE_BILLING_INFO_FAILURE_MESSAGE_DISPLAYED);
        }
      });
  }

  setUpdateBillingInfoErrorToastMessage(): void {
    const contactNumber = this.util.getContactNumber(this.contractData?.baseContract ?? '');
    this.updateBillingInfoErrorMessage = {
      message: `We couldn't update your billing information. Please contact us for assistance at`,
      link: contactNumber,
      linkText: this.util.formatPhone(contactNumber)
    };
    this.showUpdateBillingInfoErrorToast = true;
  }
  
  setGaButtonClickEvent(): void {
    const buttonNameMap: { [key: string]: string } = {
      'Credit Card': BILLING_PREFERENCES_UPDATED_CC,
      'Bank Account': BILLING_PREFERENCES_UPDATED_BANK,
      // Add more mappings here as you add payment types
    };
  
    const buttonName = buttonNameMap[this.paymentType] || 'UNKNOWN_PAYMENT_TYPE';
  
    const source = this.data.navigationSource$.getValue();
    let buttonLocation = BUTTON_LOCATION_PAYMENTS_FROM_UNKNOWN;
    switch (source) {
      case PAGE_SOURCE.PAYMENTS:
        buttonLocation = BUTTON_LOCATION_PAYMENTS_FROM_HISTORY;
        break;
      case PAGE_SOURCE.PROFILE:
        buttonLocation = BUTTON_LOCATION_PAYMENTS_FROM_PROFILE;
        break;
      case PAGE_SOURCE.MY_PLAN:
        buttonLocation = BUTTON_LOCATION_PAYMENTS_FROM_MYPLANS; // Update if there's a specific location for My Plan
        break;
      case PAGE_SOURCE.DASHBOARD:
        buttonLocation = BUTTON_LOCATION_PAYMENTS_FROM_DASHBOARD;
        break;
      case PAGE_SOURCE.SERVICE_REQUESTS:
        buttonLocation = BUTTON_LOCATION_PAYMENTS_FROM_SERVICE_REQUESTS;
        break;
      default:
        buttonLocation = BUTTON_LOCATION_PAYMENTS_FROM_UNKNOWN;
        break;
    }
    this.gaService.buttonClickEvent(buttonLocation, buttonName);
  }

  ngOnDestroy(): void {
    if (this.navigateBackTimeoutId) {
      clearTimeout(this.navigateBackTimeoutId);
    }
    this.cleaner$.next();
    this.cleaner$.complete();
  }

}
